import { useCallback, useEffect, useState } from 'react';
import './App.css';
import { ADMIN_AUTH_FAILURE_EVENT, adminApi } from './api';
import { clearAuthToken, getAuthToken, persistAuthToken } from './auth';
import LoginPage from './components/LoginPage';
import Landing from './components/landing';
import { isGoogleLoginEnabled } from './deploymentEdition';
import {
  buildAdminGoogleLoginUrl,
  consumeAdminGoogleLoginCallback,
} from './googleAuth';

function App() {
  const [googleLoginCallback] = useState(() => consumeAdminGoogleLoginCallback());
  const [authState, setAuthState] = useState({ status: 'loading', user: null, error: '' });

  const endSession = useCallback((error = '') => {
    clearAuthToken();
    setAuthState({ status: 'anonymous', user: null, error });
  }, []);

  useEffect(() => {
    let isCurrent = true;
    const isGoogleLoginCallback = Boolean(googleLoginCallback.authToken);
    const authToken = googleLoginCallback.authToken || getAuthToken();

    if (googleLoginCallback.error) {
      endSession(googleLoginCallback.error);
      return () => {
        isCurrent = false;
      };
    }

    if (!authToken) {
      setAuthState({ status: 'anonymous', user: null, error: '' });
      return () => {
        isCurrent = false;
      };
    }

    adminApi.get('/users/verify_token', {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(({ data }) => {
        if (!isCurrent) return;
        if (data?.isAdminUser !== true) {
          endSession('This account does not have administrator privileges.');
          return;
        }
        if (isGoogleLoginCallback && !persistAuthToken(authToken)) {
          endSession('Unable to save the administrator session in this browser.');
          return;
        }
        setAuthState({ status: 'authenticated', user: data, error: '' });
      })
      .catch(() => {
        if (isCurrent) {
          endSession(
            isGoogleLoginCallback
              ? 'Google sign-in could not be verified. Please try again.'
              : 'Your session is invalid or has expired. Please sign in again.'
          );
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [endSession, googleLoginCallback]);

  useEffect(() => {
    const handleAuthFailure = () => {
      endSession('Your administrator session is no longer valid. Please sign in again.');
    };
    window.addEventListener(ADMIN_AUTH_FAILURE_EVENT, handleAuthFailure);
    return () => window.removeEventListener(ADMIN_AUTH_FAILURE_EVENT, handleAuthFailure);
  }, [endSession]);

  const handleLogin = async (credentials) => {
    try {
      const { data } = await adminApi.post('/users/login', credentials);
      if (!data?.authToken) {
        throw new Error('The server did not return an authentication token.');
      }
      if (data.isAdminUser !== true) {
        throw new Error('This account does not have administrator privileges.');
      }
      if (!persistAuthToken(data.authToken)) {
        throw new Error('Unable to save the administrator session in this browser.');
      }

      setAuthState({ status: 'authenticated', user: data, error: '' });
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || 'Unable to sign in.';
      throw new Error(message);
    }
  };

  if (authState.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070b14] text-sm text-gray-400">
        Verifying administrator session...
      </div>
    );
  }

  if (authState.status !== 'authenticated') {
    const googleLoginUrl = isGoogleLoginEnabled() ? buildAdminGoogleLoginUrl() : '';
    return (
      <LoginPage
        onLogin={handleLogin}
        googleLoginUrl={googleLoginUrl}
        initialError={authState.error}
      />
    );
  }

  return (
    <div className="App">
      <Landing user={authState.user} onLogout={() => endSession()} />
    </div>
  );
}

export default App;
